import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalInput, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { TerminalSelect, TerminalLabel, TerminalTextarea, TerminalAlert, TerminalLoading } from './shared/TerminalForms';


const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
`;

const Title = styled(TerminalTitle)`
  margin-bottom: 2rem;
  font-size: 2rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const OperationTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.active ? theme.colors.bgSecondary : 'transparent'};
  border: 1px solid ${props => props.active ? theme.colors.borderBright : theme.colors.border};
  color: ${props => props.active ? theme.colors.primary : theme.colors.text};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: ${theme.borderRadius.sm};
  
  &:hover {
    border-color: ${theme.colors.borderBright};
    background: ${theme.colors.overlayLight};
    color: ${theme.colors.primaryLight};
  }
  
  ${props => props.active && `
    box-shadow: ${theme.shadows.glow};
  `}
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const ResultCard = styled(TerminalCard)`
  margin-top: 2rem;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const ResultTitle = styled.h2`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.success};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '[SUCCESS] ';
    color: ${theme.colors.success};
  }
`;

const ResultInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text};
`;

const ResultGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const ResultItem = styled.div`
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  border: 1px solid ${theme.colors.border};
`;

const ResultItemLabel = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const ResultItemValue = styled.div`
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  color: ${theme.colors.text};
  word-break: break-all;
  background: ${theme.colors.bgTertiary};
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.border};
`;

const CopyButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: 1px solid ${theme.colors.borderBright};
  color: ${theme.colors.text};
  font-family: ${theme.fonts.mono};
  font-size: 0.75rem;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  margin-left: 0.5rem;
  transition: all ${theme.transitions.fast};
  text-transform: uppercase;
  
  &:hover {
    background: ${theme.colors.overlayLight};
    box-shadow: ${theme.shadows.glow};
  }
`;

const Cryptography = () => {
  const [operation, setOperation] = useState('encrypt');
  const [algorithm, setAlgorithm] = useState('aes-256-gcm');
  const [plaintext, setPlaintext] = useState('Hello World! This is a secret message.');
  const [ciphertext, setCiphertext] = useState('');
  const [key, setKey] = useState('');
  const [iv, setIv] = useState('');
  const [tag, setTag] = useState('');
  const [decryptedText, setDecryptedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [algorithms, setAlgorithms] = useState([]);
  
  const [hashData, setHashData] = useState('Sensitive data to hash');
  const [hashAlgorithm, setHashAlgorithm] = useState('sha256');
  const [hashEncoding, setHashEncoding] = useState('base64');
  
  const [password, setPassword] = useState('MyStrongPassword123!');
  const [salt, setSalt] = useState('');
  const [iterations, setIterations] = useState(1000);
  const [keyLength, setKeyLength] = useState(32);
  const [deriveAlgorithm, setDeriveAlgorithm] = useState('pbkdf2');
  const [hashAlgorithmForDerive, setHashAlgorithmForDerive] = useState('sha256');
  
  const [randomLength, setRandomLength] = useState(32);
  const [randomEncoding, setRandomEncoding] = useState('base64');
  const [randomType, setRandomType] = useState('bytes');

  const fetchAlgorithms = async () => {
    try {
      const response = await api.get('/algorithms');
      setAlgorithms(response.data.algorithms || []);
    } catch (err) {
      console.error('Failed to fetch algorithms:', err);
    }
  };

  useEffect(() => {
    fetchAlgorithms();
  }, []);

  const handleEncrypt = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/encrypt',
        {
          plaintext: plaintext,
          algorithm: algorithm
        }
      );

      setResult(response.data);
      if (response.data.success && response.data.data) {
        setCiphertext(response.data.data.ciphertext || '');
        setKey(response.data.data.key || '');
        setIv(response.data.data.iv || '');
        setTag(response.data.data.tag || '');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred during encryption');
    } finally {
      setLoading(false);
    }
  };

  const handleDecrypt = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        ciphertext: ciphertext,
        algorithm: algorithm,
        key: key,
        iv: iv
      };

      if (tag) {
        payload.tag = tag;
      }

      const response = await api.post('/decrypt',
        payload
      );

      setResult(response.data);
      if (response.data.success && response.data.data) {
        setDecryptedText(response.data.data.plaintext || '');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred during decryption');
    } finally {
      setLoading(false);
    }
  };

  const handleHash = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/hash',
        {
          data: hashData,
          algorithm: hashAlgorithm,
          encoding: hashEncoding
        }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred during hash generation');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDerivation = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        password: password,
        iterations: iterations,
        keyLength: keyLength,
        algorithm: deriveAlgorithm,
        hashAlgorithm: hashAlgorithmForDerive
      };

      if (salt) {
        payload.salt = salt;
      }

      const response = await api.post('/derive',
        payload
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred during key derivation');
    } finally {
      setLoading(false);
    }
  };

  const handleRandom = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/random',
        {
          length: randomLength,
          encoding: randomEncoding,
          type: randomType
        }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred during random generation');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearAll = () => {
    setPlaintext('');
    setCiphertext('');
    setKey('');
    setIv('');
    setTag('');
    setDecryptedText('');
    setHashData('Sensitive data to hash');
    setPassword('MyStrongPassword123!');
    setSalt('');
    setRandomLength(32);
    setResult(null);
    setError(null);
  };

  const getOperationHandler = () => {
    switch (operation) {
      case 'encrypt': return handleEncrypt;
      case 'decrypt': return handleDecrypt;
      case 'hash': return handleHash;
      case 'derive': return handleKeyDerivation;
      case 'random': return handleRandom;
      default: return handleEncrypt;
    }
  };

  const getOperationButtonText = () => {
    switch (operation) {
      case 'encrypt': return 'Encrypt';
      case 'decrypt': return 'Decrypt';
      case 'hash': return 'Generate Hash';
      case 'derive': return 'Derive Key';
      case 'random': return 'Generate Random';
      default: return 'Encrypt';
    }
  };

  return (
    <Container>
      <Title>Cryptography Tools</Title>

      <TerminalCard>
        <OperationTabs>
          <Tab active={operation === 'encrypt'} onClick={() => setOperation('encrypt')}>
            Encrypt
          </Tab>
          <Tab active={operation === 'decrypt'} onClick={() => setOperation('decrypt')}>
            Decrypt
          </Tab>
          <Tab active={operation === 'hash'} onClick={() => setOperation('hash')}>
            Hash
          </Tab>
          <Tab active={operation === 'derive'} onClick={() => setOperation('derive')}>
            Derive
          </Tab>
          <Tab active={operation === 'random'} onClick={() => setOperation('random')}>
            Random
          </Tab>
        </OperationTabs>

        <FormGrid>
          {(operation === 'encrypt' || operation === 'decrypt') && (
            <>
              <FormGroup>
                <TerminalLabel>Algorithm</TerminalLabel>
                <TerminalSelect
                  value={algorithm}
                  onChange={(e) => setAlgorithm(e.target.value)}
                >
                  {algorithms.map(algo => (
                    <option key={algo.value} value={algo.value}>
                      {algo.name} - {algo.description}
                    </option>
                  ))}
                </TerminalSelect>
              </FormGroup>

              {operation === 'encrypt' && (
                <FormGroup>
                  <TerminalLabel>Plaintext</TerminalLabel>
                  <TerminalTextarea
                    value={plaintext}
                    onChange={(e) => setPlaintext(e.target.value)}
                    placeholder="Enter text to encrypt..."
                  />
                </FormGroup>
              )}

              {operation === 'decrypt' && (
                <>
                  <FormGroup>
                    <TerminalLabel>Ciphertext</TerminalLabel>
                    <TerminalTextarea
                      value={ciphertext}
                      onChange={(e) => setCiphertext(e.target.value)}
                      placeholder="Enter encrypted text..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <TerminalLabel>Key (Base64)</TerminalLabel>
                    <TerminalInput
                      type="text"
                      value={key}
                      onChange={(e) => setKey(e.target.value)}
                      placeholder="Enter decryption key..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <TerminalLabel>IV (Base64)</TerminalLabel>
                    <TerminalInput
                      type="text"
                      value={iv}
                      onChange={(e) => setIv(e.target.value)}
                      placeholder="Enter initialization vector..."
                    />
                  </FormGroup>

                  <FormGroup>
                    <TerminalLabel>Tag (Base64) - Optional</TerminalLabel>
                    <TerminalInput
                      type="text"
                      value={tag}
                      onChange={(e) => setTag(e.target.value)}
                      placeholder="Enter authentication tag..."
                    />
                  </FormGroup>
                </>
              )}
            </>
          )}

          {operation === 'hash' && (
            <>
              <FormGroup>
                <TerminalLabel>Data to Hash</TerminalLabel>
                <TerminalTextarea
                  value={hashData}
                  onChange={(e) => setHashData(e.target.value)}
                  placeholder="Enter data to hash..."
                />
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Hash Algorithm</TerminalLabel>
                <TerminalSelect
                  value={hashAlgorithm}
                  onChange={(e) => setHashAlgorithm(e.target.value)}
                >
                  <option value="sha256">SHA-256 - Secure Hash Algorithm 256-bit</option>
                  <option value="sha512">SHA-512 - Secure Hash Algorithm 512-bit</option>
                  <option value="sha1">SHA-1 - Secure Hash Algorithm 1 (Legacy)</option>
                  <option value="md5">MD5 - Message Digest Algorithm 5 (Legacy)</option>
                </TerminalSelect>
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Encoding</TerminalLabel>
                <TerminalSelect
                  value={hashEncoding}
                  onChange={(e) => setHashEncoding(e.target.value)}
                >
                  <option value="base64">Base64</option>
                  <option value="hex">Hexadecimal</option>
                </TerminalSelect>
              </FormGroup>
            </>
          )}

          {operation === 'derive' && (
            <>
              <FormGroup>
                <TerminalLabel>Password</TerminalLabel>
                <TerminalInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password..."
                />
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Salt (Base64) - Optional</TerminalLabel>
                <TerminalInput
                  type="text"
                  value={salt}
                  onChange={(e) => setSalt(e.target.value)}
                  placeholder="Enter salt (auto-generated if empty)..."
                />
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Iterations</TerminalLabel>
                <TerminalInput
                  type="number"
                  value={iterations}
                  onChange={(e) => setIterations(parseInt(e.target.value))}
                  min="1000"
                  max="100000"
                />
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Key Length (bytes)</TerminalLabel>
                <TerminalInput
                  type="number"
                  value={keyLength}
                  onChange={(e) => setKeyLength(parseInt(e.target.value))}
                  min="16"
                  max="64"
                />
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Algorithm</TerminalLabel>
                <TerminalSelect
                  value={deriveAlgorithm}
                  onChange={(e) => setDeriveAlgorithm(e.target.value)}
                >
                  <option value="pbkdf2">PBKDF2 - Password-Based Key Derivation Function 2</option>
                  <option value="argon2">Argon2 - Memory-hard key derivation function</option>
                </TerminalSelect>
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Hash Algorithm</TerminalLabel>
                <TerminalSelect
                  value={hashAlgorithmForDerive}
                  onChange={(e) => setHashAlgorithmForDerive(e.target.value)}
                >
                  <option value="sha256">SHA-256</option>
                  <option value="sha512">SHA-512</option>
                  <option value="sha1">SHA-1</option>
                </TerminalSelect>
              </FormGroup>
            </>
          )}

          {operation === 'random' && (
            <>
              <FormGroup>
                <TerminalLabel>Length (bytes)</TerminalLabel>
                <TerminalInput
                  type="number"
                  value={randomLength}
                  onChange={(e) => setRandomLength(parseInt(e.target.value))}
                  min="1"
                  max="1024"
                />
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Encoding</TerminalLabel>
                <TerminalSelect
                  value={randomEncoding}
                  onChange={(e) => setRandomEncoding(e.target.value)}
                >
                  <option value="base64">Base64</option>
                  <option value="hex">Hexadecimal</option>
                </TerminalSelect>
              </FormGroup>

              <FormGroup>
                <TerminalLabel>Type</TerminalLabel>
                <TerminalSelect
                  value={randomType}
                  onChange={(e) => setRandomType(e.target.value)}
                >
                  <option value="bytes">Bytes - Raw binary data</option>
                  <option value="string">String - Printable characters</option>
                </TerminalSelect>
              </FormGroup>
            </>
          )}
        </FormGrid>

        <ButtonGroup>
          <TerminalButton
            onClick={getOperationHandler()}
            disabled={loading}
          >
            {loading ? 'Processing...' : getOperationButtonText()}
          </TerminalButton>

          <TerminalButton
            onClick={clearAll}
            style={{ background: 'transparent', borderColor: theme.colors.border }}
          >
            Clear All
          </TerminalButton>
        </ButtonGroup>
      </TerminalCard>

      {error && (
        <TerminalAlert className="error" style={{ marginTop: '1rem' }}>
          {error}
        </TerminalAlert>
      )}

      {loading && (
        <TerminalLoading>
          Processing...
        </TerminalLoading>
      )}

      {result && result.success && (
        <ResultCard>
          <ResultHeader>
            <ResultTitle>Operation Successful</ResultTitle>
            <ResultInfo>
              <StatItem>
                Algorithm: {result.data?.algorithm || algorithm}
              </StatItem>
              <StatItem>
                Timestamp: {result.timestamp ? new Date(result.timestamp).toLocaleString() : 'N/A'}
              </StatItem>
            </ResultInfo>
          </ResultHeader>

          <ResultGrid>
            {operation === 'encrypt' && result.data && (
              <>
                <ResultItem>
                  <ResultItemLabel>
                    Ciphertext (Base64)
                    <CopyButton onClick={() => copyToClipboard(result.data.ciphertext)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.ciphertext}</ResultItemValue>
                </ResultItem>

                <ResultItem>
                  <ResultItemLabel>
                    Key (Base64)
                    <CopyButton onClick={() => copyToClipboard(result.data.key)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.key}</ResultItemValue>
                </ResultItem>

                <ResultItem>
                  <ResultItemLabel>
                    IV (Base64)
                    <CopyButton onClick={() => copyToClipboard(result.data.iv)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.iv}</ResultItemValue>
                </ResultItem>

                {result.data.tag && (
                  <ResultItem>
                    <ResultItemLabel>
                      Tag (Base64)
                      <CopyButton onClick={() => copyToClipboard(result.data.tag)}>
                        Copy
                      </CopyButton>
                    </ResultItemLabel>
                    <ResultItemValue>{result.data.tag}</ResultItemValue>
                  </ResultItem>
                )}
              </>
            )}

            {operation === 'decrypt' && result.data && (
              <ResultItem>
                <ResultItemLabel>
                  Decrypted Plaintext
                  <CopyButton onClick={() => copyToClipboard(result.data.plaintext)}>
                    Copy
                  </CopyButton>
                </ResultItemLabel>
                <ResultItemValue>{result.data.plaintext}</ResultItemValue>
              </ResultItem>
            )}

            {operation === 'hash' && result.data && (
              <>
                <ResultItem>
                  <ResultItemLabel>
                    Hash ({result.data.encoding || hashEncoding})
                    <CopyButton onClick={() => copyToClipboard(result.data.hash)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.hash}</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>Original Length</ResultItemLabel>
                  <ResultItemValue>{result.data.original_length} characters</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>Hash Length</ResultItemLabel>
                  <ResultItemValue>{result.data.hash_length} bytes</ResultItemValue>
                </ResultItem>
              </>
            )}

            {operation === 'derive' && result.data && (
              <>
                <ResultItem>
                  <ResultItemLabel>
                    Derived Key (Base64)
                    <CopyButton onClick={() => copyToClipboard(result.data.derivedKey)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.derivedKey}</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>
                    Salt (Base64)
                    <CopyButton onClick={() => copyToClipboard(result.data.salt)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.salt}</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>Key Length</ResultItemLabel>
                  <ResultItemValue>{result.data.keyLength} bytes</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>Iterations</ResultItemLabel>
                  <ResultItemValue>{result.data.iterations}</ResultItemValue>
                </ResultItem>
              </>
            )}

            {operation === 'random' && result.data && (
              <>
                <ResultItem>
                  <ResultItemLabel>
                    Random Data ({result.data.encoding || randomEncoding})
                    <CopyButton onClick={() => copyToClipboard(result.data.random)}>
                      Copy
                    </CopyButton>
                  </ResultItemLabel>
                  <ResultItemValue>{result.data.random}</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>Type</ResultItemLabel>
                  <ResultItemValue>{result.data.type}</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>Byte Length</ResultItemLabel>
                  <ResultItemValue>{result.data.byte_length} bytes</ResultItemValue>
                </ResultItem>
                <ResultItem>
                  <ResultItemLabel>String Length</ResultItemLabel>
                  <ResultItemValue>{result.data.string_length} characters</ResultItemValue>
                </ResultItem>
              </>
            )}
          </ResultGrid>
        </ResultCard>
      )}
    </Container>
  );
};

export default Cryptography;
